import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { WorkspaceDto, CreateWorkspaceDto } from '../dto/workspace.dto';
import {
  ProcessResult,
  QueryOptions,
  QueryResult,
  QueryResultItem,
} from '../components/common-dto/query.dto';

@Injectable({
  providedIn: 'root',
})
export class MockWorkspaceService {
  private workspaces: Map<string, WorkspaceDto> = new Map();
  private nextId = 1;

  constructor() {
    // Add some mock data
    this.addMockWorkspace({
      name: 'Main Organization',
      code: 'main-org',
      hostname: 'main.example.com',
      ownerId: 'user-1',
      ownerEmail: 'admin@example.com',
      publicSettings: {
        theme: 'light',
        logoUrl: 'https://example.com/logo1.png',
      },
      createdAt: new Date(new Date().setMonth(new Date().getMonth() - 6)),
      updatedAt: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    });

    this.addMockWorkspace({
      name: 'Development Team',
      code: 'dev-team',
      hostname: 'dev.example.com',
      ownerId: 'user-2',
      ownerEmail: 'dev-lead@example.com',
      publicSettings: {
        theme: 'dark',
        logoUrl: 'https://example.com/logo2.png',
      },
      createdAt: new Date(new Date().setMonth(new Date().getMonth() - 3)),
      updatedAt: new Date(new Date().setDate(new Date().getDate() - 5)),
    });

    this.addMockWorkspace({
      name: 'Marketing Department',
      code: 'marketing',
      hostname: 'marketing.example.com',
      ownerId: 'user-3',
      ownerEmail: 'marketing-lead@example.com',
      publicSettings: {
        theme: 'blue',
        logoUrl: 'https://example.com/logo3.png',
      },
      createdAt: new Date(new Date().setMonth(new Date().getMonth() - 1)),
      updatedAt: new Date(),
    });
  }

  private addMockWorkspace(workspace: WorkspaceDto): string {
    const id = `workspace-${this.nextId++}`;
    this.workspaces.set(id, { ...workspace });
    return id;
  }

  getWorkspaces(
    queryParams: QueryOptions
  ): Observable<QueryResult<WorkspaceDto>> {
    const items: QueryResultItem<WorkspaceDto>[] = [];
    const skip = queryParams.skip || 0;
    const take = queryParams.take || 10;

    let filteredWorkspaces = Array.from(this.workspaces.entries());

    // Apply filter if provided
    if (queryParams.filter) {
      const filterLower = queryParams.filter.toLowerCase();
      filteredWorkspaces = filteredWorkspaces.filter(
        ([_, workspace]) =>
          workspace.name.toLowerCase().includes(filterLower) ||
          workspace.code.toLowerCase().includes(filterLower) ||
          workspace.hostname.toLowerCase().includes(filterLower) ||
          (workspace.ownerEmail &&
            workspace.ownerEmail.toLowerCase().includes(filterLower))
      );
    }

    // Apply pagination
    const paginatedWorkspaces = filteredWorkspaces.slice(skip, skip + take);

    // Convert to QueryResultItems
    for (const [id, workspace] of paginatedWorkspaces) {
      items.push({
        id,
        item: { ...workspace },
      });
    }

    return of({
      items,
      total: filteredWorkspaces.length,
      skip,
      take,
    });
  }

  getWorkspace(id: string): Observable<WorkspaceDto> {
    const workspace = this.workspaces.get(id);
    if (workspace) {
      return of({ ...workspace });
    }
    return of({} as WorkspaceDto);
  }

  createWorkspace(workspace: CreateWorkspaceDto): Observable<ProcessResult> {
    const newWorkspace: WorkspaceDto = {
      ...workspace,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const id = this.addMockWorkspace(newWorkspace);

    return of({
      success: true,
      message: 'Workspace created successfully',
      id,
    });
  }

  updateWorkspace(
    id: string,
    workspace: WorkspaceDto
  ): Observable<ProcessResult> {
    if (this.workspaces.has(id)) {
      const existingWorkspace = this.workspaces.get(id);

      this.workspaces.set(id, {
        ...workspace,
        createdAt: existingWorkspace!.createdAt,
        updatedAt: new Date(),
      });

      return of({
        success: true,
        message: 'Workspace updated successfully',
        id,
      });
    }

    return of({
      success: false,
      message: 'Workspace not found',
      id,
    });
  }

  deleteWorkspace(id: string): Observable<ProcessResult> {
    if (this.workspaces.has(id)) {
      this.workspaces.delete(id);

      return of({
        success: true,
        message: 'Workspace deleted successfully',
        id,
      });
    }

    return of({
      success: false,
      message: 'Workspace not found',
      id,
    });
  }
}
